import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'

import { useLazyGetSurchargeRateQuery } from 'api/payment-gateway/paymentGateway'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import T from 'T'
import { formatDollarsToCents } from 'utils/price'

// The rate endpoint returns the surcharge for a $100.00 reference amount; we scale it to the
// actually-entered amount for the breakdown.
const REFERENCE_AMOUNT_CENTS = 10000

/**
 * Renders the surcharge disclosure (and, when a payment-method token is available, a live
 * Subtotal / Surcharge / Total breakdown for the entered amount). Replaces the older standalone
 * alert + estimate components. Mirrors what the portal's `sale`-mode Fortis iframe shows natively
 * — we render it ourselves in the CRM because the CRM iframe runs in tokenization mode.
 */
const SurchargePanel = ({ amount = 0, zip = '', token = '', accountId = '' }) => {
  const [getSurchargeRate, { data }] = useLazyGetSurchargeRateQuery()

  useEffect(() => {
    if (zip && token) {
      getSurchargeRate({ zip, token, accountId })
    }
  }, [zip, token, accountId, getSurchargeRate])

  const surchargeRate = data?.surchargeRate
  const referenceSurchargeCents = data?.surchargeAmountCents
  const amountCents = formatDollarsToCents(amount || 0)

  const breakdown = useMemo(() => {
    if (!data?.surchargeApplied || !referenceSurchargeCents || amountCents <= 0) return null
    const surchargeCents = Math.round((amountCents * referenceSurchargeCents) / REFERENCE_AMOUNT_CENTS)
    return { subtotalCents: amountCents, surchargeCents, totalCents: amountCents + surchargeCents }
  }, [data?.surchargeApplied, referenceSurchargeCents, amountCents])

  // Lead with the rate when we know it (e.g. "A 3.00% surcharge applies…"); otherwise use the
  // generic disclosure copy.
  const ratePrefix = surchargeRate ? `A ${surchargeRate}% surcharge applies to credit-card purchases. ` : ''

  return (
    <Alert
      icon={<InfoOutlinedIcon fontSize="small" />}
      severity="info"
      sx={{
        alignItems: 'flex-start',
        borderRadius: 2,
        '& .MuiAlert-message': { width: '100%', py: 0.25 },
      }}
      variant="outlined"
    >
      <Stack spacing={breakdown ? 1.25 : 0}>
        <Typography color="text.secondary" lineHeight={1.5} variant="caption">
          {`${ratePrefix}${T.TRANSACTION_SURCHARGE_DESCRIPTION}`}
        </Typography>
        {breakdown && (
          <>
            <Divider flexItem />
            <Box>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Typography color="text.secondary" variant="body2">
                  {T.SUBTOTAL}
                </Typography>
                <Typography variant="body2">
                  <HHDisplayMoney value={breakdown.subtotalCents} />
                </Typography>
              </Stack>
              <Stack alignItems="center" direction="row" justifyContent="space-between">
                <Typography color="text.secondary" variant="body2">
                  {T.SURCHARGE}
                </Typography>
                <Typography variant="body2">
                  <HHDisplayMoney value={breakdown.surchargeCents} />
                </Typography>
              </Stack>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
                mt={0.5}
                pt={0.5}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Typography fontWeight={700} variant="body2">
                  {T.TOTAL}
                </Typography>
                <Typography component="span" fontWeight={700} variant="body2">
                  <HHDisplayMoney value={breakdown.totalCents} />
                </Typography>
              </Stack>
            </Box>
          </>
        )}
      </Stack>
    </Alert>
  )
}

SurchargePanel.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  zip: PropTypes.string,
  token: PropTypes.string,
  accountId: PropTypes.string,
}

export default SurchargePanel
